/**
 * Phase 11 — Sensitivity detail surface for the Pk Campaign detail page.
 *
 * Renders four sections when `campaign.campaign_type === "sensitivity"`:
 *   - Top drivers card (top-3 first-order drivers with CI + units)
 *   - Tornado chart (server-rendered PNG via /sensitivity-tornado.png;
 *     fetched as a blob so the JWT bearer header travels with it)
 *   - Driver tornado 3D viewer (Phase 14.14, the third 3D)
 *   - Response-curves grid (only when method === "one_at_a_time" since
 *     variance_first_order doesn't produce per-variable curves)
 *
 * No new charting library: tornado is a server-rendered PNG; response
 * curves + 3D viewer use the existing Plotly install Phase 8c brought in.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";
import SensitivityDriver3D from "@/components/SensitivityDriver3D";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api as apiClient } from "@/api/client";

function fmt(v, d = 3) {
    if (v == null || Number.isNaN(v)) return "—";
    return Number(v).toFixed(d);
}

function PrettyVarId({ vId }) {
    return (
        <span className="font-mono text-xs">{vId}</span>
    );
}

function TopDriversCard({ topDrivers, method }) {
    if (!topDrivers || topDrivers.length === 0) return null;
    return (
        <Card data-testid="pk-sensitivity-top-drivers">
            <CardHeader>
                <CardTitle className="text-base">
                    Top drivers · first-order sensitivity
                </CardTitle>
            </CardHeader>
            <CardContent>
                <ol className="space-y-2">
                    {topDrivers.map((d) => (
                        <li
                            key={d.var_id}
                            className="flex items-baseline gap-3 border-b border-white/5 pb-2 last:border-b-0"
                            data-testid={`pk-sensitivity-driver-${d.var_id}`}
                        >
                            <span className="text-sm font-bold text-amber-300">
                                #{d.rank}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-baseline gap-2">
                                    <PrettyVarId vId={d.var_id} />
                                    <Badge variant="outline" className="text-[10px]">
                                        {d.category}
                                    </Badge>
                                    {d.unit && d.unit !== "-" && (
                                        <span className="text-xs text-muted-foreground">
                                            ({d.unit})
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {method === "variance_first_order"
                                        ? "Largest impact"
                                        : "Pk-spread across sweep"
                                    }: <span className="font-medium text-foreground">
                                        {fmt(d.index)}
                                    </span>
                                    {d.ci_95_lo !== d.ci_95_hi && (
                                        <span> ± [{fmt(d.ci_95_lo)}, {fmt(d.ci_95_hi)}]</span>
                                    )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
            </CardContent>
        </Card>
    );
}

function TornadoCard({ campaignId, hasIndices }) {
    const [src, setSrc] = useState("");
    const [err, setErr] = useState("");
    useEffect(() => {
        if (!hasIndices) return;
        let cancelled = false;
        let url = "";
        (async () => {
            try {
                const res = await apiClient.get(
                    `/api/pk-campaigns/${campaignId}/sensitivity-tornado.png`,
                    { responseType: "blob" },
                );
                if (cancelled) return;
                url = URL.createObjectURL(res.data);
                setSrc(url);
            } catch (e) {
                if (!cancelled) {
                    setErr(e?.response?.data?.detail || "Failed to load tornado");
                }
            }
        })();
        return () => {
            cancelled = true;
            if (url) URL.revokeObjectURL(url);
        };
    }, [campaignId, hasIndices]);
    if (!hasIndices) return null;
    return (
        <Card data-testid="pk-sensitivity-tornado-card">
            <CardHeader>
                <CardTitle className="text-base">
                    Sensitivity tornado
                </CardTitle>
            </CardHeader>
            <CardContent>
                {err ? (
                    <div
                        className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200"
                        data-testid="pk-sensitivity-tornado-error"
                    >
                        {err}
                    </div>
                ) : src ? (
                    <img
                        src={src}
                        alt="Sensitivity tornado chart"
                        className="w-full rounded-md border border-white/10"
                        data-testid="pk-sensitivity-tornado-img"
                    />
                ) : (
                    <div
                        className="py-8 text-center text-sm text-muted-foreground"
                        data-testid="pk-sensitivity-tornado-loading"
                    >
                        Rendering tornado…
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function ResponseCurveMini({ vId, response, onExpand }) {
    const layout = useMemo(() => ({
        autosize: true, height: 180,
        margin: { l: 36, r: 12, t: 8, b: 28 },
        paper_bgcolor: "rgba(15,23,42,0)",
        plot_bgcolor: "rgba(15,23,42,0)",
        xaxis: { color: "#94a3b8", gridcolor: "#1e293b", title: "" },
        yaxis: {
            color: "#94a3b8", gridcolor: "#1e293b",
            range: [0, 1], title: "",
        },
        showlegend: false,
    }), []);
    const data = useMemo(() => [
        // CI band
        {
            x: response.sweep_values,
            y: response.ci_hi,
            mode: "lines", line: { color: "rgba(56,189,248,0.0)", width: 0 },
            showlegend: false, type: "scatter", hoverinfo: "skip",
        },
        {
            x: response.sweep_values,
            y: response.ci_lo,
            mode: "lines", line: { color: "rgba(56,189,248,0.0)", width: 0 },
            fill: "tonexty", fillcolor: "rgba(56,189,248,0.18)",
            showlegend: false, type: "scatter", hoverinfo: "skip",
        },
        {
            x: response.sweep_values,
            y: response.pk_estimates,
            mode: "lines+markers",
            line: { color: "#38bdf8", width: 2 },
            marker: { color: "#38bdf8", size: 6 },
            type: "scatter",
            hovertemplate: "x=%{x}<br>Pk=%{y:.3f}<extra></extra>",
        },
    ], [response]);
    return (
        <button
            type="button"
            onClick={() => onExpand(vId)}
            className="text-left rounded-md border border-white/10 bg-white/5 p-2 hover:border-cyan-400/50"
            data-testid={`pk-sensitivity-curve-${vId}`}
        >
            <div className="text-xs font-mono text-foreground truncate">
                {vId}
            </div>
            <Plot
                data={data}
                layout={layout}
                config={{ displayModeBar: false, responsive: true }}
                useResizeHandler
                style={{ width: "100%", height: 180 }}
            />
        </button>
    );
}

function ResponseCurvesGrid({ oatResponses }) {
    const [expanded, setExpanded] = useState(null);
    const ids = Object.keys(oatResponses || {});
    if (ids.length === 0) return null;
    return (
        <Card data-testid="pk-sensitivity-curves-grid">
            <CardHeader>
                <CardTitle className="text-base">
                    Response curves · Pk vs variable value
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {ids.map((vId) => (
                        <ResponseCurveMini
                            key={vId}
                            vId={vId}
                            response={oatResponses[vId]}
                            onExpand={setExpanded}
                        />
                    ))}
                </div>
                <Dialog open={!!expanded} onOpenChange={(o) => !o && setExpanded(null)}>
                    <DialogContent className="max-w-3xl">
                        <DialogHeader>
                            <DialogTitle>
                                <span className="font-mono text-base">{expanded}</span>
                            </DialogTitle>
                        </DialogHeader>
                        {expanded && oatResponses[expanded] && (
                            <ResponseCurveExpanded response={oatResponses[expanded]} />
                        )}
                    </DialogContent>
                </Dialog>
            </CardContent>
        </Card>
    );
}

function ResponseCurveExpanded({ response }) {
    const layout = {
        autosize: true, height: 380,
        margin: { l: 56, r: 24, t: 16, b: 48 },
        paper_bgcolor: "rgba(15,23,42,0)",
        plot_bgcolor: "rgba(15,23,42,0)",
        xaxis: { color: "#cbd5e1", gridcolor: "#1e293b", title: "Variable value" },
        yaxis: {
            color: "#cbd5e1", gridcolor: "#1e293b",
            range: [0, 1], title: "Pk",
        },
        showlegend: false,
    };
    const data = [
        {
            x: response.sweep_values, y: response.ci_hi,
            mode: "lines", line: { color: "rgba(56,189,248,0)", width: 0 },
            showlegend: false, type: "scatter", hoverinfo: "skip",
        },
        {
            x: response.sweep_values, y: response.ci_lo,
            mode: "lines", line: { color: "rgba(56,189,248,0)", width: 0 },
            fill: "tonexty", fillcolor: "rgba(56,189,248,0.18)",
            showlegend: false, type: "scatter", hoverinfo: "skip",
        },
        {
            x: response.sweep_values, y: response.pk_estimates,
            mode: "lines+markers",
            line: { color: "#38bdf8", width: 2 },
            marker: { color: "#38bdf8", size: 8 },
            type: "scatter",
            hovertemplate: "x=%{x}<br>Pk=%{y:.3f}<extra></extra>",
        },
    ];
    return (
        <Plot
            data={data}
            layout={layout}
            config={{ displayModeBar: false, responsive: true }}
            useResizeHandler
            style={{ width: "100%", height: 380 }}
        />
    );
}

export default function SensitivitySection({ campaign }) {
    if (!campaign || campaign.campaign_type !== "sensitivity") return null;
    const sens = campaign.sensitivity_results;
    if (!sens) {
        // Campaign is sensitivity but still running — show a placeholder.
        return (
            <Card data-testid="pk-sensitivity-pending">
                <CardHeader>
                    <CardTitle className="text-base">
                        Sensitivity analysis
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                    Indices and tornado will populate once the campaign completes.
                </CardContent>
            </Card>
        );
    }
    return (
        <div className="space-y-4" data-testid="pk-sensitivity-section">
            <TopDriversCard
                topDrivers={sens.top_drivers}
                method={sens.method}
            />
            <TornadoCard
                campaignId={campaign.id}
                hasIndices={Object.keys(sens.indices || {}).length > 0}
            />
            {/* Phase 14.14 — Sensitivity-driver 3D viewer (the third 3D).
                Mounted only when status=complete + at least one ranked
                driver is available. PM-required testid:
                sensitivity-detail-driver-tornado-3d-card. */}
            {campaign.status === "complete" && sens.top_drivers?.length > 0 && (
                <Card data-testid="sensitivity-detail-driver-tornado-3d-card">
                    <CardHeader>
                        <CardTitle className="text-base">
                            Sensitivity drivers (3D)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <SensitivityDriver3D campaign={campaign} />
                    </CardContent>
                </Card>
            )}
            <ResponseCurvesGrid oatResponses={sens.oat_responses} />
        </div>
    );
}
